import React, { useState, useEffect, useContext, useRef } from 'react'
import axios from 'axios'
import { useNavigate } from 'react-router-dom'
import '../styles/Feed.css'
import Post from './Post'
import { AuthContext } from '../context/AuthContext'
import { ThemeContext } from '../context/ThemeContext'
import config from '../config'
import pfp from '../assets/addUser.png'
import { allowedHTML, formatTimeSince, timeSince } from '../helper/PostHelper'
import Select from 'react-select'
import Footer from './Footer'
import {
  customStylesPlusTrans,
  customStylesTrans,
  customStyles,
  LoadingSpinner,
  formatCurrency as fc,
  formatModifiedTicker,
} from '../helper/StylesHelper'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTimes } from '@fortawesome/free-solid-svg-icons'
import {
  buySellOptions,
  handleQuickPostSaveToDrafts,
  handleQuickPostSubmit,
} from '../helper/FeedHelper'
import { fetchUser, followUser } from '../helper/ProfileHelper'
import {
  postTypeOptions,
  categoryOptions,
  sentimentOptions,
  sortOptions,
  timeOptions,
  sortOptionsTrades,
  tradeTypeOptions,
} from '../helper/FeedHelper'
import DOMPurify from 'dompurify'
import useResponsiveWidth from '../hooks/useResponsiveWidth'
import TransactionDetails from './TransactionDetails'
import {
  fetchAllPortfolios,
  fetchUserCashBalance,
  fetchTickerSuggestions,
  getDailyChange,
  handleAddCash,
  handleSellAsset,
  handleBuyAsset,
} from '../helper/MarketsHelper'
import useClickOutside from '../hooks/useClickOutside'

/**
 * @typedef {import('../helper/PostHelper').PostObject} PostObject
 * @typedef {import('../helper/ProfileHelper').UserObject} UserObject
 * */

const Feed = () => {
  useResponsiveWidth()

  const [activeTab, setActiveTab] = useState('all')
  const [activeTabTrade, setActiveTabTrade] = useState('all')

  const [ready, setReady] = useState(false)
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [tradesLoading, setTradesLoading] = useState(true)

  const [keywords, setKeywords] = useState([])
  const [newKeyword, setNewKeyword] = useState('')

  /** @type {[PostObject[], Function]} */
  const [posts, setPosts] = useState([])

  // simply a string of transaction ids
  const [trades, setTrades] = useState([])
  const [hasMoreTrades, setHasMoreTrades] = useState(true)

  /** @type {[PostObject[], Function]} */
  const [bookmarkedPosts, setBookmarkedPosts] = useState([])
  const [trendingPosts, setTrendingPosts] = useState([])

  const { userToken, userId, isAuthenticated } = useContext(AuthContext)
  const { tc } = useContext(ThemeContext)

  /** @type {[UserObject, Function]} */
  const [currentUser, setCurrentUser] = useState(null)

  //Filtering
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [selectedTime, setSelectedTime] = useState('Today')
  const [selectedSort, setSelectedSort] = useState('Most popular')

  const [selectedTimeTrade, setSelectedTimeTrade] = useState('Today')
  const [selectedSortTrade, setSelectedSortTrade] = useState('Best trades')
  const [selectedTradeType, setSelectedTradeType] = useState('All')
  const [selectedBuySell, setSelectedBuySell] = useState('Buy')

  const [selectedPostTypes, setSelectedPostTypes] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [selectedSentiments, setSelectedSentiments] = useState([])

  // State to keep track of selected filters (no longer needed separately)
  const [selectedFilters, setSelectedFilters] = useState([])

  //Quick post
  const [quickPostContent, setQuickPostContent] = useState('')

  //Quick post categories/types
  //eslint-disable-next-line
  const [selectedPostTypesQuick, setSelectedPostTypesQuick] = useState([])
  //eslint-disable-next-line
  const [selectedCategoriesQuick, setSelectedCategoriesQuick] = useState([])
  const [selectedCategoryQuick, setSelectedCategoryQuick] = useState([])
  const [selectedPostTypeQuick, setSelectedPostTypeQuick] = useState('')

  //Trending accounts and user search
  const [editTrending, setEditTrending] = useState(false)
  const [editTrendingAccounts, setEditTrendingAccounts] = useState([])
  const [trendingAccounts, setTrendingAccounts] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)

  const [trendingAccountsLoading, setTrendingAccountsLoading] = useState(true)
  const [trendingPostsLoading, setTrendingPostsLoading] = useState(true)

  //Pagination
  const viewMoreRef = useRef(null)
  const page = useRef(0)
  const [hasMorePosts, setHasMorePosts] = useState(true)

  const viewMoreRefTrades = useRef(null)
  const pageTrades = useRef(0)

  const [addTransactionModalOpen, setAddTransactionModalOpen] = useState(false)
  const [selectedTransactionCategory, setSelectedTransactionCategory] =
    useState('Buy')
  const [userPortfolios, setUserPortfolios] = useState([])
  const [selectedPortfolio, setSelectedPortfolio] = useState(null)
  const [userCashBalance, setUserCashBalance] = useState(0)
  const [selectedAssetPrice, setSelectedAssetPrice] = useState(null)
  const [assetSuggestions, setAssetSuggestions] = useState([])
  const [selectedAsset, setSelectedAsset] = useState(null)
  const [placeholderText, setPlaceholderText] = useState('Select asset')
  const [quantity, setQuantity] = useState(0)
  const [shareToFeed, setShareToFeed] = useState(false)
  const [shareToFeedContent, setShareToFeedContent] = useState('')
  const [openFiltersModal, setOpenFiltersModal] = useState(false)
  const { visibleSection } = useContext(AuthContext)
  const [mobileLoader, setMobileLoader] = useState(true)
  let debounceTimer = useRef(null)

  const debounce = (func, delay) => {
    return function (...args) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      debounceTimer.current = setTimeout(() => {
        func.apply(this, args)
      }, delay)
    }
  }

  const performTickerFetch = (query) => {
    fetchTickerSuggestions(query, setAssetSuggestions, false, 'all')
  }

  const debouncedTickerFetch = debounce(performTickerFetch, 500)

  const handleTickerInputChange = (inputValue) => {
    if (inputValue.trim().length > 0) {
      debouncedTickerFetch(inputValue)
    } else {
      setAssetSuggestions([])
    }
  }

  useEffect(() => {
    fetchAllPortfolios(
      isAuthenticated,
      userToken,
      setUserPortfolios,
      setSelectedPortfolio
    )
  }, [addTransactionModalOpen, isAuthenticated, userToken])

  useEffect(() => {
    if (selectedPortfolio) {
      fetchUserCashBalance(
        isAuthenticated,
        userToken,
        setUserCashBalance,
        selectedPortfolio._id
      )
    }
  }, [selectedPortfolio, userPortfolios, isAuthenticated, userToken])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)

    // Cleanup on component unmount
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // useEffect(() => {
  //   if (isMobile) {
  //     const timer = setTimeout(() => {
  //       setMobileLoader(false) // Stop loading after 10 seconds
  //     }, 12000)

  //     return () => clearTimeout(timer) // Cleanup timer on component unmount
  //   } else {
  //     setMobileLoader(false) // Immediately stop loading on non-mobile devices
  //   }
  // }, [isMobile])

  useEffect(() => {
    if (userPortfolios) {
      setSelectedPortfolio(
        userPortfolios.find((pf) => pf._id === selectedPortfolio?._id) || null
      )
    }
  }, [userPortfolios, selectedPortfolio?._id])

  const placeholderUser = {
    _id: null,
    username: 'Placeholder',
    profilePicture: pfp,
    trendingRank: null,
  }

  const ensureArrayLength8 = (accounts) => {
    const filledAccounts = [...accounts]
    for (let i = 0; i < 8; i++) {
      if (!filledAccounts[i]) {
        filledAccounts[i] = { ...placeholderUser, trendingRank: i + 1 }
      }
    }
    return filledAccounts
  }

  const handleFollowToggle = async (accountId) => {
    try {
      const newStatus = await followUser(userId, accountId, userToken)

      setTrendingAccounts((prevAccounts) =>
        prevAccounts.map((account) =>
          account._id === accountId
            ? { ...account, isFollowing: newStatus }
            : account
        )
      )
    } catch (error) {
      console.error('Error toggling follow status:', error)
    }
  }

  const handleSearch = async (query) => {
    setSearchQuery(query)
    if (query.length > 0) {
      try {
        const response = await axios.get(
          `${config.backendUrl}/api/auth/search-users`,
          {
            params: { query },
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }
        )
        setSearchResults(response.data)
      } catch (error) {
        console.error('Error searching users:', error)
      }
    } else {
      setSearchResults([])
    }
  }

  const handleUserSelect = (user) => {
    setSelectedUser(user)
    setSearchQuery('')
    setSearchResults([])
  }

  useEffect(() => {
    if (selectedUser && selectedAccountIndex !== null) {
      handleSubmitEdit()
    }
    //eslint-disable-next-line
  }, [selectedUser])

  const handleSubmitEdit = () => {
    if (selectedUser && selectedAccountIndex !== null) {
      const userAlreadyExists = editTrendingAccounts.some(
        (account) => account._id === selectedUser._id
      )

      if (userAlreadyExists) {
        alert('This user is already in the trending accounts.')
        return
      }

      const updatedAccounts = [...editTrendingAccounts]
      updatedAccounts[selectedAccountIndex] = {
        ...selectedUser,
        trendingRank: selectedAccountIndex + 1,
      }

      setEditTrendingAccounts(updatedAccounts)
      setAnchorEl(null)
      setSelectedUser(null)
    }
  }

  const handleCancelEdit = () => {
    setEditTrending(false)
    setEditTrendingAccounts(trendingAccounts)
  }

  const handleSaveTrendingAccounts = async () => {
    try {
      await axios.put(
        `${config.backendUrl}/api/auth/edit-trending-accounts`,
        {
          accounts: editTrendingAccounts.map((account) => ({
            userId: account._id || null,
            username: account.username || null,
            trendingRank: account.trendingRank,
          })),
        },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      )

      setTrendingAccounts(editTrendingAccounts)
      setEditTrending(false)
      alert('Trending accounts updated successfully.')
    } catch (error) {
      console.error('Error updating trending accounts:', error)
      alert('Failed to update trending accounts.')
    }
  }

  const [postsLoading, setPostsLoading] = useState(false)
  const [autoLoadTriggered, setAutoLoadTriggered] = useState(false)
  const [initialLoadDone, setInitialLoadDone] = useState(false)
  const fetchPostsRef = useRef(false) // Track if fetchPosts is already running

  const fetchPosts = async (flag = '') => {
    console.log(
      `fetchPosts called with flag: ${flag}, postsLoading: ${postsLoading}, fetchPostsRef: ${fetchPostsRef.current}, page: ${page.current}`
    )

    if (postsLoading || fetchPostsRef.current) {
      console.log('Preventing duplicate call - already running')
      return // Prevent multiple simultaneous calls
    }

    // Set both state and ref to prevent duplicates
    setPostsLoading(true)
    fetchPostsRef.current = true

    // add delay after setting loading state
    // await delay(1000); // Reduced delay since we now prevent duplicates properly

    // Only show main loading spinner for the initial load
    if (page.current === 0) {
      setLoading(true)
    }

    try {
      var postLimit = 20
      const response = await axios.get(`${config.backendUrl}/api/posts`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
        params: {
          page: page.current,
          limit: postLimit,
          selectedPostTypes: selectedPostTypes.map((type) => type),
          selectedCategories: selectedCategories.map((category) => category),
          selectedSentiments: selectedSentiments.map((sentiment) => sentiment),
          selectedTime,
          selectedSort,
          activeTab,
          keywords,
          currentUserId: userId,
        },
      })

      if (page.current === 0) {
        // First load - replace posts
        setPosts(response.data)
        setAutoLoadTriggered(false) // Reset auto-load flag
        setInitialLoadDone(true)

        // Auto-load next 20 after first 20 are loaded
        if (response.data.length === postLimit && !autoLoadTriggered) {
          setAutoLoadTriggered(true)
          setTimeout(() => {
            page.current = 1
            fetchPostsRef.current = false // Reset ref before next call
            setPostsLoading(false) // Reset loading state before next call
            fetchPosts('auto-load')
          }, 1000) // 1 second delay to let the first batch render
          return // Don't reset postsLoading in finally block for this case
        }
      } else {
        // Subsequent loads - append posts
        setPosts((prev) => [...prev, ...response.data])
      }

      setHasMorePosts(response.data.length === postLimit)
    } catch (error) {
      console.error('Error fetching posts:', error)
    } finally {
      setLoading(false)
      setPostsLoading(false)
      fetchPostsRef.current = false // Reset ref when done
    }
  }

  const fetchTrades = async () => {
    setTradesLoading(true)
    try {
      var publicTxnLimit = 20
      const response = await axios.get(
        `${config.backendUrl}/api/stockActions/publicTransactions`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
          params: {
            page: pageTrades.current,
            limit: publicTxnLimit,
            selectedTime: selectedTimeTrade,
            selectedSort: selectedSortTrade,
            selectedTradeType: selectedTradeType,
            selectedBuySell: selectedBuySell,
            activeTab: activeTabTrade,
          },
        }
      )

      if (pageTrades.current === 0) {
        setTrades(response.data)
      } else {
        setTrades((prev) => [...prev, ...response.data])
      }
      setHasMoreTrades(response.data.length === publicTxnLimit)
    } catch (error) {
      console.error('Error fetching trades:', error)
    }
    setTradesLoading(false)
  }

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

  useEffect(() => {
    // Prevent duplicate initial loads using multiple checks
    if (
      postsLoading ||
      fetchPostsRef.current ||
      (page.current === 0 && initialLoadDone)
    ) {
      console.log('Skipping duplicate initial load - already running or done')
      return
    }

    console.log('Starting initial load - useEffect triggered')
    page.current = 0
    setPosts([])
    setAutoLoadTriggered(false) // Reset auto-load flag when filters change
    setInitialLoadDone(false) // Reset initial load flag
    fetchPosts('initial')
    // Removed setTimeout - auto-loading is now handled in fetchPosts function
    //eslint-disable-next-line
  }, [
    selectedFilters,
    selectedPostTypes,
    selectedCategories,
    selectedSentiments,
    selectedTime,
    selectedSort,
    keywords,
    activeTab,
  ])

  useEffect(() => {
    pageTrades.current = 0
    setTrades([])
    fetchTrades()
  }, [
    selectedTimeTrade,
    selectedSortTrade,
    activeTabTrade,
    selectedTradeType,
    selectedBuySell,
  ]) //eslint-disable-line

  useEffect(() => {
    const fetchTrendingAccounts = async () => {
      setTrendingAccountsLoading(true)
      try {
        const response = await axios.get(
          `${config.backendUrl}/api/auth/trending-accounts`,
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }
        )
        const filledTrendingAccounts = ensureArrayLength8(response.data)
        setTrendingAccounts(filledTrendingAccounts)
        setEditTrendingAccounts(filledTrendingAccounts)
      } catch (error) {
        console.error('Error fetching posts:', error)
      }
      setTrendingAccountsLoading(false)
    }

    const fetchCurrentUser = async () => {
      try {
        const fetchedUser = await fetchUser(userId, userToken)
        setCurrentUser(fetchedUser.data)
      } catch (error) {
        console.error('Error fetching current user:', error)
      }
    }

    const fetchBookmarkedPosts = async () => {
      try {
        const response = await axios.get(`${config.backendUrl}/api/bookmarks`, {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        })
        setBookmarkedPosts(response.data)
      } catch (error) {
        console.error('Error fetching bookmarked posts:', error)
      }
      setReady(true)
    }

    const fetchTrendingPosts = async () => {
      setTrendingPostsLoading(true)
      try {
        const response = await axios.get(
          `${config.backendUrl}/api/posts/trending-posts`,
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
            },
          }
        )
        setTrendingPosts(response.data)
      } catch (error) {
        console.error('Error fetching trending posts:', error)
      }
      setTrendingPostsLoading(false)
    }

    const fetchData = async () => {
      await fetchCurrentUser()
      // Remove fetchPosts() from here - it's already called in the other useEffect
      await fetchBookmarkedPosts()
      await fetchTrendingAccounts()
      await fetchTrendingPosts()
      await fetchTrades()
    }

    fetchData()
    //eslint-disable-next-line
  }, [userToken, userId])

  const navigateToProfile = (username) => {
    if (!isAuthenticated) {
      alert('Please sign in to view profiles')
      return
    }
    navigate(`/profile/${username}`)
  }

  const handleEditTrending = () => {
    if (!currentUser?.isAdmin) {
      alert('You are not authorized to edit trending accounts')
      return
    }
    setEditTrending(!editTrending)
  }

  useEffect(() => {
    renderContent()
    //eslint-disable-next-line
  }, [posts, currentUser])

  useEffect(() => {
    const currentRef = viewMoreRef.current
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMorePosts &&
          !loading &&
          !postsLoading &&
          !fetchPostsRef.current
        ) {
          page.current += 1
          fetchPosts('intersection-observer')
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
      }
    )

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
    // eslint-disable-next-line
  }, [hasMorePosts, loading, postsLoading])

  const refreshFollowingStatus = (post) => {
    post.isAuthorFollowed = !post.isAuthorFollowed
  }

  const renderContent = () => {
    const filteredPosts = posts

    if (filteredPosts.length === 0 && !loading) {
      return <div style={{ textAlign: 'center' }}>No posts found</div>
    }
    return (
      <div>
        {filteredPosts.flatMap((post) => {
          const postElements = [
            <Post
              key={post._id}
              postId={post._id}
              profilePic={post.author.profilePicture}
              author={post.author.name}
              verified={post.author.isUserVerified}
              username={post.author.username}
              time={formatTimeSince(timeSince(post.timePosted))}
              content={post.content}
              tradeDetails={post.tradeDetails}
              title={post.title}
              sentiment={post.sentiment}
              type={post.type}
              categories={post.categories}
              taggedAssets={post.taggedAssets}
              onProfileClick={() => navigateToProfile(post.author.username)}
              bookmarked={post.isBookmarked}
              onBookmarkClick={() =>
                handleBookmarkClick(post._id, post.isBookmarked)
              }
              isQuotePost={post.isQuotePost}
              quotePostId={post.quotePostId}
              isCopyRepost={false}
              images={post.images}
              isUpvoted={post.isUpvoted}
              isDownvoted={post.isDownvoted}
              isAdmin={currentUser?.isAdmin}
              caption={post.caption}
              refreshFeed={() =>
                setPosts((posts) => posts.filter((p) => p._id !== post._id))
              }
              isAuthorFollowed={
                post.isAuthorFollowed || userId === post.author._id
              }
              authorId={post.author._id}
              refreshFollowingStatus={() => refreshFollowingStatus(post)}
              upvoteCountParam={post.upvoteCount}
              downvoteCountParam={post.downvoteCount}
              commentCountParam={post.commentCount}
              repostCountParam={post.repostCount}
              isCopyRepostedParam={post.isCopyReposted}
              transactionId={post.transactionId}
            />,
          ]

          if (selectedSort === 'Most recent') {
            postElements.push(
              ...post.copyReposts.map((reposterId) => (
                <Post
                  key={`${post._id}-${reposterId}`}
                  postId={post._id}
                  profilePic={post.author.profilePicture}
                  author={post.author.name}
                  verified={post.author.isUserVerified}
                  username={post.author.username}
                  taggedAssets={post.taggedAssets}
                  time={formatTimeSince(timeSince(post.timePosted))}
                  content={post.content}
                  tradeDetails={post.tradeDetails}
                  title={post.title}
                  sentiment={post.sentiment}
                  type={post.type}
                  categories={post.categories}
                  onProfileClick={() =>
                    navigateToProfile(post.author.username, post.author._id)
                  }
                  bookmarked={post.isBookmarked}
                  onBookmarkClick={() =>
                    handleBookmarkClick(post._id, post.isBookmarked)
                  }
                  isQuotePost={post.isQuotePost}
                  quotePostId={post.quotePostId}
                  isCopyRepost={true}
                  copyReposterId={reposterId}
                  images={post.images}
                  isUpvoted={post.isUpvoted}
                  isDownvoted={post.isDownvoted}
                  isAdmin={currentUser?.isAdmin}
                  caption={post.caption}
                  refreshFeed={() =>
                    setPosts((posts) => posts.filter((p) => p._id !== post._id))
                  }
                  isAuthorFollowed={
                    post.isAuthorFollowed || userId === post.author._id
                  }
                  authorId={post.author._id}
                  refreshFollowingStatus={refreshFollowingStatus}
                  upvoteCountParam={post.upvoteCount}
                  downvoteCountParam={post.downvoteCount}
                  commentCountParam={post.commentCount}
                  repostCountParam={post.repostCount}
                  isCopyRepostedParam={post.isCopyReposted}
                  transactionId={post.transactionId}
                />
              ))
            )
          }

          return postElements
        })}

        {loading && (
          <div style={{ textAlign: 'center', margin: '1rem 0' }}>
            <LoadingSpinner />
          </div>
        )}
        {hasMorePosts && !loading && !postsLoading && (
          <button
            ref={viewMoreRef}
            style={{ margin: '1rem auto 0px' }}
            className="cp-AddElementButton"
            onClick={() => {
              if (!postsLoading && !fetchPostsRef.current) {
                page.current += 1
                fetchPosts('view-more-button')
              }
            }}
          >
            View more
          </button>
        )}
        {!hasMorePosts && !loading && posts.length > 0 && (
          <p style={{ textAlign: 'center', margin: '1rem 0' }}>
            No more posts to show
          </p>
        )}
      </div>
    )
  }

  const handleAddKeyword = (keyword) => {
    if (keywords.includes(keyword) || keyword.trim() === '') {
      alert('Keyword already added or empty')
      return
    }

    setKeywords((prevKeywords) => [...prevKeywords, keyword.trim()])
    setNewKeyword('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddKeyword(newKeyword)
    }
  }

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter((k) => k !== keyword))
  }

  const handleBookmarkClick = async (postId, isBookmarked) => {
    try {
      const url = `${config.backendUrl}/api/bookmarks/${postId}`
      const method = isBookmarked ? 'delete' : 'post'
      await axios({
        method,
        url,
        headers: { Authorization: `Bearer ${userToken}` },
      })
      const fetchBookmarkedPosts = async () => {
        try {
          const response = await axios.get(
            `${config.backendUrl}/api/bookmarks`,
            {
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
            }
          )
          setBookmarkedPosts(response.data)
        } catch (error) {
          console.error('Error fetching bookmarked posts:', error)
        }
      }
      fetchBookmarkedPosts()
    } catch (error) {
      console.error('Error updating bookmark:', error)
    }
  }

  const handleTimeChangeTrade = (selectedOption) => {
    setSelectedTimeTrade(selectedOption.value)
  }

  const handleSortTradeChange = (selectedOption) => {
    setSelectedSortTrade(selectedOption.value)
  }

  const handleTradeTypeChange = (selectedOption) => {
    setSelectedTradeType(selectedOption?.value)
  }

  const handleBuySellChange = (selectedOption) => {
    setSelectedBuySell(selectedOption.value)
  }

  const handleSortChange = (selectedOption) => {
    setSelectedSort(selectedOption.value)
  }

  const handleReset = () => {
    setSelectedPostTypes([])
    setSelectedCategories([])
    setSelectedSentiments([])
    setKeywords([])
    setSelectedTime('Today')
  }

  const handleInputChange = (e) => {
    setNewKeyword(e.target.value)
  }

  const handleAccountClick = (event, index) => {
    setAnchorEl(event.currentTarget)
    setSelectedAccountIndex(index)
  }

  const getExcerpt = (content) => {
    const words = content.split(' ')
    const excerpt = words.slice(0, 30).join(' ')
    return words.length > 30 ? `${excerpt}...` : excerpt
  }

  const renderFeaturedPosts = () => {
    if (trendingPostsLoading) {
      return <LoadingSpinner />
    }
    if (trendingPosts.length === 0) {
      return <p>No trending posts available.</p>
    }

    return (
      <div className="featured-posts-list">
        {trendingPosts.map((post) => (
          <div
            className="featured-post-item"
            onClick={() => navigate(`/post/${post._id}`)}
            key={post._id}
          >
            <p className="featured-post-title bold">
              {post.title ? (
                post.title
              ) : (
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      getExcerpt(post.content),
                      allowedHTML
                    ),
                  }}
                />
              )}
            </p>
            <p className="featured-post-author">by {post.author.name}</p>
          </div>
        ))}
      </div>
    )
  }

  const renderWelcomeMessage = () => {
    return (
      <div className="FeedWelcome-container">
        <div className="FeedWelcome-content">
          <h1 className="FeedWelcome-title">Join the finance super app 🚀</h1>
          <p className="FeedWelcome-description">
            Join leaderboards. Copy trades. Share trades. Buy options, stocks,
            crypto. Laugh at your friends. Love Mr. Vestr.{' '}
            <span className="FeedWelcome-highlight bold">
              The retail revolution, in one product.
            </span>
          </p>
        </div>
        <button
          onClick={() => navigate('/signin')}
          className="CreatePostFeedButton greenButton moreButtonPadding FeedWelcome-create-account-btn"
        >
          Create account
        </button>
      </div>
    )
  }

  const portfolioOptions = userPortfolios.map((pf) => ({
    value: pf._id,
    label: pf.name,
  }))

  useEffect(() => {
    let interval
    if (selectedAsset) {
      getDailyChange(selectedAsset, setSelectedAssetPrice)
      interval = setInterval(() => {
        getDailyChange(selectedAsset, setSelectedAssetPrice)
      }, 10000)
    }
    return () => clearInterval(interval)
  }, [selectedAsset])

  const renderTransactionModal = () => {
    if (!addTransactionModalOpen) {
      return null
    }

    const handlePortfolioChange = (selectedOption) => {
      const newPortfolio = userPortfolios.find(
        (pf) => pf._id === selectedOption.value
      )
      setSelectedPortfolio(newPortfolio || null)
    }

    const selectedPortfolioOption = selectedPortfolio
      ? portfolioOptions.find(
          (option) => option.value === selectedPortfolio._id
        ) || null
      : null

    return (
      <div className="FeedTransactionModal" ref={modalRef}>
        <h2 className="FeedPostHeaderH2">Add Transaction</h2>
        <div className="Feed-toggle-container">
          <button
            className={`Feed-toggle-button bold ${
              selectedTransactionCategory === 'Buy' ? 'Feed-active' : ''
            }`}
            onClick={() => setSelectedTransactionCategory('Buy')}
          >
            Buy
          </button>
          <button
            className={`Feed-toggle-button bold ${
              selectedTransactionCategory === 'Sell' ? 'Feed-active' : ''
            }`}
            onClick={() => setSelectedTransactionCategory('Sell')}
          >
            Sell
          </button>
          <>{/* Add Cash Code */}</>
        </div>

        <div className="FeedPortfolioSelect">
          <Select
            id="portfolio-select"
            className="FeedPortfolioDropdown"
            value={selectedPortfolioOption}
            onChange={handlePortfolioChange}
            options={portfolioOptions}
            placeholder="Select a portfolio"
            isClearable={false}
            isSearchable={false}
            unstyled
            styles={customStyles(tc(), 'var(--font-med-l)', '3.8rem')}
          />
          {selectedTransactionCategory === 'Buy' ||
          selectedTransactionCategory === 'Add cash' ? (
            <span className="FeedPortfolioSpan">{`You have $${fc(
              userCashBalance,
              2
            )} available in this portfolio.`}</span>
          ) : (
            selectedTransactionCategory === 'Sell' &&
            (selectedAsset ? (
              <span className="FeedPortfolioSpan">
                {`You have ${fc(
                  selectedPortfolio?.assets.find(
                    (asset) => asset.ticker === selectedAsset
                  )?.quantity || 0,
                  2
                )} ($${fc(
                  (selectedPortfolio?.assets.find(
                    (asset) => asset.ticker === selectedAsset
                  )?.quantity || 0) * selectedAssetPrice || 0,
                  2
                )}) shares of ${selectedAsset} available in this portfolio.`}
              </span>
            ) : (
              <span className="FeedPortfolioSpan">
                {`Select an asset to view available shares.`}
              </span>
            ))
          )}
        </div>

        {selectedTransactionCategory !== 'Add cash' && (
          <div className="FeedPortfolioSelect">
            <Select
              value={
                selectedAsset
                  ? { value: selectedAsset, label: selectedAsset }
                  : null
              }
              onChange={(selectedOption) => {
                setSelectedAsset(selectedOption ? selectedOption.value : null)
              }}
              onInputChange={(inputValue) =>
                handleTickerInputChange(inputValue)
              }
              options={assetSuggestions.map((suggestion) => ({
                value: suggestion.ticker,
                label: `${formatModifiedTicker(suggestion.ticker)} - ${
                  suggestion.name
                }`,
              }))}
              isSearchable
              placeholder={placeholderText}
              onFocus={() => setPlaceholderText('Search for a ticker')}
              onBlur={() => setPlaceholderText('Select asset')}
              noOptionsMessage={() => null}
              unstyled
              styles={customStyles(tc(), 'var(--font-med-l)', '3.8rem')}
            />
            <span className="FeedPortfolioSpan">
              {`Price per share`} <br />
              {selectedAssetPrice ? `$${fc(selectedAssetPrice, 2)}` : '--'}
            </span>
          </div>
        )}

        {selectedAsset &&
          (selectedTransactionCategory === 'Buy' ||
            selectedTransactionCategory === 'Sell') && (
            <div className="FeedInputContainer">
              <div className="FeedInputRow">
                <label className="FeedLabel">Quantity</label>
                <input
                  type="number"
                  className="FeedInputField"
                  placeholder="0"
                  min="0"
                  max={
                    selectedTransactionCategory === 'Sell'
                      ? selectedPortfolio?.assets.find(
                          (asset) => asset.ticker === selectedAsset
                        )?.quantity || 0
                      : null
                  }
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
              <div className="FeedInputRow">
                <label className="FeedLabel">Transaction total</label>
                <input
                  type="text"
                  className="FeedInputField"
                  placeholder="$0"
                  value={`$${fc(quantity * selectedAssetPrice, 2)}`}
                  readOnly
                />
              </div>
              <div className="FeedShareTransaction">
                <div className="FeedToggleRow">
                  <label className="FeedLabel">Share to feed</label>
                  <div
                    className="Feed-toggle-container"
                    style={{ width: '30%' }}
                  >
                    <button
                      className={`Feed-toggle-button bold ${
                        shareToFeed ? 'Feed-active' : ''
                      }`}
                      onClick={() => setShareToFeed(true)}
                    >
                      Yes
                    </button>
                    <button
                      className={`Feed-toggle-button bold ${
                        !shareToFeed ? 'Feed-active' : ''
                      }`}
                      onClick={() => setShareToFeed(false)}
                    >
                      No
                    </button>
                  </div>
                </div>
                {shareToFeed && (
                  <textarea
                    className="FeedInputField"
                    placeholder="Write a caption..."
                    value={shareToFeedContent}
                    onChange={(e) => setShareToFeedContent(e.target.value)}
                    style={{ resize: 'none' }}
                  />
                )}
              </div>
            </div>
          )}

        {selectedTransactionCategory === 'Add cash' && (
          <div className="FeedInputContainer">
            <div className="FeedInputRow">
              <label className="FeedLabel">Amount</label>
              <input
                type="number"
                className="FeedInputField"
                placeholder="0"
                min="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="Feed-buy-wrapper">
          <div
            className="Feed-toggle-container"
            style={{ gap: '1rem', border: 'none' }}
          >
            <button
              onClick={() => handleFeedTransaction()}
              className="Feed-toggle-button bold Feed-active"
            >
              {selectedTransactionCategory}
            </button>
            <button
              className="Feed-toggle-button bold"
              onClick={() => handleTransactionModalClose()}
              style={{
                backgroundColor: 'var(--background-color)',
                borderRadius: '5rem',
                color: 'var(--text-color)',
              }}
            >
              Close
            </button>
          </div>
          {selectedTransactionCategory === 'Sell' && (
            <span className="FeedPortfolioSpan">
              {`Total must be between $0.00 and $${fc(
                (selectedPortfolio?.assets.find(
                  (asset) => asset.ticker === selectedAsset
                )?.quantity || 0) * selectedAssetPrice,
                2
              )}`}
            </span>
          )}
          {selectedTransactionCategory === 'Buy' && (
            <span className="FeedPortfolioSpan">
              {`Total must be between $0.00 and $${fc(userCashBalance, 2)}`}
            </span>
          )}
        </div>
      </div>
    )
  }

  const handleFeedTransaction = async () => {
    if (shareToFeed && shareToFeedContent.trim() === '') {
      alert('Please enter a caption to share to the feed')
      return
    }
    const callback = (transactionId) => {
      fetchAllPortfolios(isAuthenticated, userToken, setUserPortfolios)
      if (shareToFeed && transactionId) {
        handleQuickPostSubmit(
          shareToFeedContent,
          null,
          null,
          transactionId,
          userToken
        )
      }
      alert(`${selectedTransactionCategory} transaction successful`)
    }
    if (selectedTransactionCategory === 'Add cash') {
      handleAddCash(quantity, userToken, selectedPortfolio?._id, callback)
    } else if (selectedTransactionCategory === 'Buy') {
      handleBuyAsset(
        isAuthenticated,
        userToken,
        quantity,
        selectedAsset,
        selectedPortfolio?._id,
        callback
      )
    } else if (selectedTransactionCategory === 'Sell') {
      handleSellAsset(
        isAuthenticated,
        userToken,
        quantity,
        selectedAsset,
        selectedPortfolio?._id,
        callback
      )
    }
  }

  const handleTransactionModalClose = () => {
    setAddTransactionModalOpen(false)
    setSelectedPortfolio(null)
    setSelectedAsset(null)
    setSelectedAssetPrice(null)
  }

  const modalRef = useClickOutside(
    handleTransactionModalClose,
    addTransactionModalOpen
  )

  const toggleFilterSelection = (value, selectionArray, setSelectionArray) => {
    if (selectionArray.includes(value)) {
      setSelectionArray(selectionArray.filter((v) => v !== value))
    } else {
      setSelectionArray([...selectionArray, value])
    }
  }
  const filterButtonRef = useRef(null)
  const filtersModalRef = useClickOutside(
    () => setOpenFiltersModal(false),
    openFiltersModal,
    filterButtonRef
  )

  return (
    <>
      {renderTransactionModal()}

      <div className="FeedContainer">
        {!isAuthenticated && renderWelcomeMessage()}
        {!isMobile && (
          <div className="FeedMainContent">
            <div
              className={`FeedBackdrop ${
                addTransactionModalOpen ? 'active' : ''
              }`}
            />
            <div className="FeedContent" id="feedContent">
              <div className="FeedPostHeader">
                <h2 className="FeedPostHeaderH2">Posts</h2>
                <div className="FeedNavItems">
                  {['All', 'Following', 'Friends', 'Bookmarks'].map((tab) => (
                    <button
                      key={tab}
                      className={`CreatePostFeedButton ${
                        activeTab.toLowerCase() === tab.toLowerCase()
                          ? 'greenButton'
                          : ''
                      }`}
                      onClick={() => {
                        if (!isAuthenticated && tab !== 'All') {
                          alert('Please sign in to view this tab')
                          return
                        }
                        setActiveTab(tab.toLowerCase())
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                <div className="FeedInputWrapper">
                  <textarea
                    className="FeedInputBar"
                    placeholder="Write something to the Vestr community!"
                    value={quickPostContent}
                    onChange={(e) => {
                      setQuickPostContent(e.target.value)
                      e.target.style.height = 'auto'
                      e.target.style.height = `${e.target.scrollHeight}px`
                    }}
                  />

                  <div
                    style={{
                      display: 'flex',
                      gap: '1rem',
                      alignItems: 'center',
                      alignSelf: 'flex-end',
                    }}
                  >
                    <button
                      className="CreatePostFeedBtn"
                      onClick={() => {
                        if (!isAuthenticated) {
                          alert('Please sign in to create a post')
                          return
                        }
                        navigate('/createpost', {
                          state: {
                            quickPostContent: quickPostContent,
                            selectedCategoryQuick: selectedCategoryQuick,
                            selectedPostTypeQuick: selectedPostTypeQuick,
                            fromFeed: true,
                          },
                        })
                      }}
                    >
                      Full View
                    </button>

                    <button
                      className="CreatePostFeedBtn"
                      onClick={async () => {
                        if (!isAuthenticated) {
                          alert('Please sign in to create a post')
                          return
                        }
                        if (!quickPostContent) {
                          alert('Content cannot be empty')
                          return
                        }
                        const response = await handleQuickPostSaveToDrafts(
                          quickPostContent,
                          userToken
                        )
                        if (response.status === 201) {
                          alert('Post saved to drafts')
                          setQuickPostContent('')
                        }
                      }}
                    >
                      Save to drafts
                    </button>

                    <button
                      className="CreatePostFeedBtn"
                      style={{
                        color: 'var(--stock-change-pos)',
                        backgroundColor: 'transparent',
                        border: '0.05rem solid var(--stock-change-pos)',
                      }}
                      onClick={async () => {
                        if (!isAuthenticated) {
                          alert('Please sign in to create a post')
                          return
                        }
                        if (!quickPostContent) {
                          alert('Content cannot be empty')
                          return
                        }
                        const response = await handleQuickPostSubmit(
                          quickPostContent,
                          selectedCategoryQuick,
                          selectedPostTypeQuick,
                          null,
                          userToken
                        )
                        if (response.status === 201) {
                          alert('Post submitted successfully')
                          setQuickPostContent('')
                          setSelectedCategoriesQuick([])
                          setSelectedCategoryQuick([])
                          setSelectedPostTypesQuick([])
                          setSelectedPostTypeQuick('')
                        } else {
                          alert('Error submitting post')
                        }
                      }}
                    >
                      Post
                    </button>
                  </div>
                </div>
              </div>
              <div className="FeedSubHeader"></div>
              <div className="FeedSortBarWrapper">
                <div className="FeedSortItems">
                  <Select
                    value={sortOptions.find(
                      (option) => option.value === selectedSort
                    )}
                    onChange={handleSortChange}
                    options={sortOptions}
                    isSearchable={false}
                    unstyled
                    styles={customStylesTrans(tc())}
                  />
                  {/* Sort stays here, timeframe & filters moved to modal */}
                  <div className="FeedFilterWrapper">
                    <button
                      ref={filterButtonRef}
                      onClick={(e) => {
                        e.stopPropagation()
                        setOpenFiltersModal((prev) => !prev)
                      }}
                      className="FeedFilterButton"
                      style={{ backgroundColor: 'transparent' }}
                    >
                      Filters +
                    </button>

                    {openFiltersModal && (
                      <div className="FeedFiltersModal" ref={filtersModalRef}>
                        <div className="FeedFiltersModalContent">
                          <input
                            className="FeedFiltersModalSearch"
                            type="text"
                            placeholder="Search keyword"
                            value={newKeyword}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                          />
                          <div className="FeedFiltersKeywordsContainer">
                            {keywords.length === 0 ? (
                              <span className="FeedNoKeywords">
                                No keywords added
                              </span>
                            ) : (
                              keywords.map((keyword) => (
                                <div
                                  className="FeedSearchBarKeyword"
                                  style={{ width: 'fit-content' }}
                                  key={keyword}
                                >
                                  {keyword}
                                  <span className="keyword-x">
                                    <FontAwesomeIcon
                                      icon={faTimes}
                                      onClick={() =>
                                        handleRemoveKeyword(keyword)
                                      }
                                    />
                                  </span>
                                </div>
                              ))
                            )}
                          </div>

                          <div className="FeedFiltersSection">
                            <p className="FeedFiltersTitle">Timeframe</p>
                            <div className="FeedFiltersOptionsRow">
                              {timeOptions.map((time) => (
                                <button
                                  key={time.label}
                                  className={`CreatePostFeedButton ${
                                    selectedTime === time.value
                                      ? 'greenButton'
                                      : ''
                                  }`}
                                  onClick={() => setSelectedTime(time.value)}
                                >
                                  {time.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="FeedFiltersSection">
                            <p className="FeedFiltersTitle">Post type</p>
                            <div className="FeedFiltersOptionsRow">
                              {postTypeOptions.map((type) => (
                                <button
                                  key={type.label}
                                  className={`CreatePostFeedButton ${
                                    selectedPostTypes.includes(type.value)
                                      ? 'greenButton'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    toggleFilterSelection(
                                      type.value,
                                      selectedPostTypes,
                                      setSelectedPostTypes
                                    )
                                  }
                                >
                                  {type.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="FeedFiltersSection">
                            <p className="FeedFiltersTitle">Sentiment</p>
                            <div className="FeedFiltersOptionsRow">
                              {sentimentOptions.map((sent) => (
                                <button
                                  key={sent.label}
                                  className={`CreatePostFeedButton ${
                                    selectedSentiments.includes(sent.value)
                                      ? 'greenButton'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    toggleFilterSelection(
                                      sent.value,
                                      selectedSentiments,
                                      setSelectedSentiments
                                    )
                                  }
                                >
                                  {sent.label}
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="FeedFiltersSection">
                            <p className="FeedFiltersTitle">Category</p>
                            <div className="FeedFiltersOptionsRow">
                              {categoryOptions.map((cat) => (
                                <button
                                  key={cat.value}
                                  className={`CreatePostFeedButton ${
                                    selectedCategories.includes(cat.value)
                                      ? 'greenButton'
                                      : ''
                                  }`}
                                  onClick={() =>
                                    toggleFilterSelection(
                                      cat.value,
                                      selectedCategories,
                                      setSelectedCategories
                                    )
                                  }
                                >
                                  {cat.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div
                            className="FeedFiltersOptionsRow"
                            style={{ marginTop: '2rem' }}
                          >
                            <button
                              className="CreatePostFeedButton"
                              onClick={handleReset}
                            >
                              Clear filters
                            </button>
                            <button
                              className="CreatePostFeedButton"
                              onClick={() => setOpenFiltersModal(false)}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {ready ? renderContent() : <LoadingSpinner />}
            </div>
            <div id="sidebar" className="RightSidebar">
              <h2 className="FeedPostHeaderH2">Trades</h2>
              <div className="FeedNavItems">
                {['All', 'Following', 'Friends'].map((tab) => (
                  <button
                    key={tab}
                    className={`CreatePostFeedButton ${
                      activeTabTrade.toLowerCase() === tab.toLowerCase()
                        ? 'greenButton'
                        : ''
                    }`}
                    onClick={() => {
                      if (!isAuthenticated && tab !== 'All') {
                        alert('Please sign in to view this tab')
                        return
                      }
                      setActiveTabTrade(tab.toLowerCase())
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              <div className="FeedSortBarWrapper">
                <div className="FeedSortBar" style={{ paddingTop: '0rem' }}>
                  <div className="FeedAddTransaction">
                    <button
                      onClick={() => setAddTransactionModalOpen(true)}
                      className="FeedAddTransactionBtn bold"
                    >
                      Add Transaction +
                    </button>
                  </div>
                  <div
                    className="FeedSortItems"
                    style={{ marginBottom: '2rem' }}
                  >
                    <Select
                      className="FeedSortButton"
                      value={sortOptionsTrades.find(
                        (option) =>
                          option.value.toLowerCase() ===
                          selectedSortTrade.toLowerCase()
                      )}
                      onChange={handleSortTradeChange}
                      options={sortOptionsTrades}
                      isSearchable={false}
                      unstyled
                      styles={customStylesTrans(tc())}
                    />
                    <Select
                      className="FeedSortButton"
                      value={timeOptions.find(
                        (option) =>
                          option.value.toLowerCase() ===
                          selectedTimeTrade.toLowerCase()
                      )}
                      onChange={handleTimeChangeTrade}
                      options={timeOptions}
                      isSearchable={false}
                      unstyled
                      styles={customStylesTrans(tc())}
                      title="Filter by time"
                    />
                    <Select
                      className="FeedPostType"
                      value={tradeTypeOptions.find(
                        (option) =>
                          option.value.toLowerCase() ===
                          selectedTradeType.toLowerCase()
                      )}
                      onChange={handleTradeTypeChange}
                      options={tradeTypeOptions}
                      unstyled
                      isSearchable={false}
                      styles={customStylesTrans(tc())}
                      placeholder="Type"
                    />
                    <Select
                      className="FeedPostType"
                      value={buySellOptions.find(
                        (option) =>
                          option.value.toLowerCase() ===
                          selectedBuySell.toLowerCase()
                      )}
                      onChange={handleBuySellChange}
                      options={buySellOptions}
                      unstyled
                      isSearchable={false}
                      styles={customStylesTrans(tc())}
                      placeholder="Buy/Sell"
                    />
                  </div>
                </div>
              </div>

              <div className="FeedTradesContainer">
                {trades.map((trade) => (
                  <TransactionDetails key={trade} transactionId={trade} />
                ))}

                {tradesLoading && (
                  <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                    <LoadingSpinner />
                  </div>
                )}

                {hasMoreTrades && !tradesLoading && (
                  <button
                    ref={viewMoreRefTrades}
                    style={{ margin: '0 auto', marginTop: '1rem' }}
                    className="cp-AddElementButton"
                    onClick={() => {
                      pageTrades.current += 1
                      fetchTrades()
                    }}
                  >
                    View more
                  </button>
                )}

                {!hasMoreTrades && !tradesLoading && trades.length > 0 && (
                  <p style={{ textAlign: 'center', margin: '1rem 0' }}>
                    No more trades to show
                  </p>
                )}

                {!tradesLoading && trades.length === 0 && (
                  <p style={{ textAlign: 'center', margin: '1rem 0' }}>
                    No trades available
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {isMobile && (
          <div className="FeedMainContent">
            {visibleSection === 'posts' && (
              <div className="FeedContent" id="feedContent">
                <div className="FeedPostHeader">
                  <h2 className="FeedPostHeaderH2">Posts</h2>
                  <div className="FeedNavItems">
                    {['All', 'Following', 'Friends'].map((tab) => (
                      <button
                        key={tab}
                        className={`CreatePostFeedButton ${
                          activeTab.toLowerCase() === tab.toLowerCase()
                            ? 'greenButton'
                            : ''
                        }`}
                        onClick={() => {
                          if (!isAuthenticated && tab !== 'All') {
                            alert('Please sign in to view this tab')
                            return
                          }
                          setActiveTab(tab.toLowerCase())
                        }}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="FeedInputWrapper">
                    <textarea
                      className="FeedInputBar"
                      placeholder="Write something to the Vestr community!"
                      value={quickPostContent}
                      onChange={(e) => {
                        setQuickPostContent(e.target.value)
                        e.target.style.height = 'auto'
                        e.target.style.height = `${e.target.scrollHeight}px`
                      }}
                    />

                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'center',
                        alignSelf: 'flex-end',
                      }}
                    >
                      <button
                        className="CreatePostFeedBtn"
                        onClick={() => {
                          if (!isAuthenticated) {
                            alert('Please sign in to create a post')
                            return
                          }
                          navigate('/createpost', {
                            state: {
                              quickPostContent: quickPostContent,
                              selectedCategoryQuick: selectedCategoryQuick,
                              selectedPostTypeQuick: selectedPostTypeQuick,
                              fromFeed: true,
                            },
                          })
                        }}
                      >
                        Full View
                      </button>

                      <button
                        className="CreatePostFeedBtn"
                        onClick={async () => {
                          if (!isAuthenticated) {
                            alert('Please sign in to create a post')
                            return
                          }
                          if (!quickPostContent) {
                            alert('Content cannot be empty')
                            return
                          }
                          const response = await handleQuickPostSaveToDrafts(
                            quickPostContent,
                            userToken
                          )
                          if (response.status === 201) {
                            alert('Post saved to drafts')
                            setQuickPostContent('')
                          }
                        }}
                      >
                        Save to drafts
                      </button>

                      <button
                        className="CreatePostFeedBtn"
                        style={{
                          color: 'var(--stock-change-pos)',
                          backgroundColor: 'transparent',
                          border: '0.05rem solid var(--stock-change-pos)',
                        }}
                        onClick={async () => {
                          if (!isAuthenticated) {
                            alert('Please sign in to create a post')
                            return
                          }
                          if (!quickPostContent) {
                            alert('Content cannot be empty')
                            return
                          }
                          const response = await handleQuickPostSubmit(
                            quickPostContent,
                            selectedCategoryQuick,
                            selectedPostTypeQuick,
                            null,
                            userToken
                          )
                          if (response.status === 201) {
                            alert('Post submitted successfully')
                            setQuickPostContent('')
                            setSelectedCategoriesQuick([])
                            setSelectedCategoryQuick([])
                            setSelectedPostTypesQuick([])
                            setSelectedPostTypeQuick('')
                          } else {
                            alert('Error submitting post')
                          }
                        }}
                      >
                        Post
                      </button>
                    </div>
                  </div>
                </div>
                <div className="FeedSubHeader"></div>
                <div className="FeedSortBarWrapper">
                  <div className="LeadSortWrapper">
                    <Select
                      className="FeedSortButton"
                      value={sortOptions.find(
                        (option) => option.value === selectedSort
                      )}
                      onChange={handleSortChange}
                      options={sortOptions}
                      isSearchable={false}
                      unstyled
                      styles={customStylesTrans(tc())}
                    />
                    {/* Sort stays here, timeframe & filters moved to modal */}
                    <div className="FeedFilterWrapper">
                      <button
                        ref={filterButtonRef}
                        onClick={(e) => {
                          e.stopPropagation()
                          setOpenFiltersModal((prev) => !prev)
                        }}
                        className="FeedFilterButton"
                        style={{
                          backgroundColor: 'transparent',
                          cursor: isMobile ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isMobile}
                      >
                        Filters +
                      </button>

                      {openFiltersModal && (
                        <div className="FeedFiltersModal" ref={filtersModalRef}>
                          <div className="FeedFiltersModalContent">
                            <input
                              className="FeedFiltersModalSearch"
                              type="text"
                              placeholder="Search keyword"
                              value={newKeyword}
                              onChange={handleInputChange}
                              onKeyDown={handleKeyDown}
                            />
                            <div className="FeedFiltersKeywordsContainer">
                              {keywords.length === 0 ? (
                                <span className="FeedNoKeywords">
                                  No keywords added
                                </span>
                              ) : (
                                keywords.map((keyword) => (
                                  <div
                                    className="FeedSearchBarKeyword"
                                    style={{ width: 'fit-content' }}
                                    key={keyword}
                                  >
                                    {keyword}
                                    <span className="keyword-x">
                                      <FontAwesomeIcon
                                        icon={faTimes}
                                        onClick={() =>
                                          handleRemoveKeyword(keyword)
                                        }
                                      />
                                    </span>
                                  </div>
                                ))
                              )}
                            </div>

                            <div className="FeedFiltersSection">
                              <p className="FeedFiltersTitle">Timeframe</p>
                              <div className="FeedFiltersOptionsRow">
                                {timeOptions.map((time) => (
                                  <button
                                    key={time.label}
                                    className={`CreatePostFeedButton ${
                                      selectedTime === time.value
                                        ? 'greenButton'
                                        : ''
                                    }`}
                                    onClick={() => setSelectedTime(time.value)}
                                  >
                                    {time.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="FeedFiltersSection">
                              <p className="FeedFiltersTitle">Post type</p>
                              <div className="FeedFiltersOptionsRow">
                                {postTypeOptions.map((type) => (
                                  <button
                                    key={type.label}
                                    className={`CreatePostFeedButton ${
                                      selectedPostTypes.includes(type.value)
                                        ? 'greenButton'
                                        : ''
                                    }`}
                                    onClick={() =>
                                      toggleFilterSelection(
                                        type.value,
                                        selectedPostTypes,
                                        setSelectedPostTypes
                                      )
                                    }
                                  >
                                    {type.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="FeedFiltersSection">
                              <p className="FeedFiltersTitle">Sentiment</p>
                              <div className="FeedFiltersOptionsRow">
                                {sentimentOptions.map((sent) => (
                                  <button
                                    key={sent.label}
                                    className={`CreatePostFeedButton ${
                                      selectedSentiments.includes(sent.value)
                                        ? 'greenButton'
                                        : ''
                                    }`}
                                    onClick={() =>
                                      toggleFilterSelection(
                                        sent.value,
                                        selectedSentiments,
                                        setSelectedSentiments
                                      )
                                    }
                                  >
                                    {sent.label}
                                  </button>
                                ))}
                              </div>
                            </div>

                            <div className="FeedFiltersSection">
                              <p className="FeedFiltersTitle">Category</p>
                              <div className="FeedFiltersOptionsRow">
                                {categoryOptions.map((cat) => (
                                  <button
                                    key={cat.value}
                                    className={`CreatePostFeedButton ${
                                      selectedCategories.includes(cat.value)
                                        ? 'greenButton'
                                        : ''
                                    }`}
                                    onClick={() =>
                                      toggleFilterSelection(
                                        cat.value,
                                        selectedCategories,
                                        setSelectedCategories
                                      )
                                    }
                                  >
                                    {cat.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div
                              className="FeedFiltersOptionsRow"
                              style={{ marginTop: '2rem' }}
                            >
                              <button
                                className="CreatePostFeedButton"
                                onClick={handleReset}
                              >
                                Clear filters
                              </button>
                              <button
                                className="CreatePostFeedButton"
                                onClick={() => setOpenFiltersModal(false)}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {!ready && (
                  <div className="FeedLoadingSpinner">
                    <LoadingSpinner />
                  </div>
                )}
                {ready && renderContent()}
              </div>
            )}
            {visibleSection === 'trades' && (
              <div id="sidebar" className="RightSidebar">
                <h2 className="FeedPostHeaderH2">Trades</h2>
                <div className="FeedNavItems">
                  {['All', 'Following', 'Friends'].map((tab) => (
                    <button
                      key={tab}
                      className={`CreatePostFeedButton ${
                        activeTabTrade.toLowerCase() === tab.toLowerCase()
                          ? 'greenButton'
                          : ''
                      }`}
                      onClick={() => {
                        if (!isAuthenticated && tab !== 'All') {
                          alert('Please sign in to view this tab')
                          return
                        }
                        setActiveTabTrade(tab.toLowerCase())
                      }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
                <div className="FeedSortBarWrapper">
                  <div className="FeedSortBar" style={{ paddingTop: '0rem' }}>
                    <div className="FeedAddTransaction">
                      <button
                        onClick={() => setAddTransactionModalOpen(true)}
                        className="FeedAddTransactionBtn bold"
                      >
                        Add Transaction +
                      </button>
                    </div>
                    <div
                      className="LeadSortWrapper"
                      style={{ marginBottom: '2rem' }}
                    >
                      <Select
                        className="FeedSortButton"
                        value={sortOptionsTrades.find(
                          (option) => option.value === selectedSortTrade
                        )}
                        onChange={handleSortTradeChange}
                        options={sortOptionsTrades}
                        isSearchable={false}
                        unstyled
                        styles={customStylesTrans(tc())}
                      />
                      <Select
                        className="FeedSortButton"
                        value={timeOptions.find(
                          (option) => option.value === selectedTimeTrade
                        )}
                        onChange={handleTimeChangeTrade}
                        options={timeOptions}
                        isSearchable={false}
                        unstyled
                        styles={customStylesTrans(tc())}
                        title="Filter by time"
                      />
                      <Select
                        className="FeedPostType"
                        value={tradeTypeOptions.find(
                          (option) => option.value === selectedTradeType
                        )}
                        onChange={handleTradeTypeChange}
                        options={tradeTypeOptions}
                        unstyled
                        isSearchable={false}
                        styles={customStylesTrans(tc())}
                        placeholder="Type"
                      />
                      <Select
                        className="FeedPostType"
                        value={buySellOptions.find(
                          (option) => option.value === selectedBuySell
                        )}
                        onChange={handleBuySellChange}
                        options={buySellOptions}
                        unstyled
                        isSearchable={false}
                        styles={customStylesTrans(tc())}
                        placeholder="Buy/Sell"
                      />
                    </div>
                  </div>
                </div>

                <div className="FeedTradesContainer">
                  {trades.map((trade) => (
                    <TransactionDetails key={trade} transactionId={trade} />
                  ))}

                  {tradesLoading && (
                    <div style={{ textAlign: 'center', margin: '1rem 0' }}>
                      <LoadingSpinner />
                    </div>
                  )}

                  {hasMoreTrades && !tradesLoading && (
                    <button
                      ref={viewMoreRefTrades}
                      style={{ margin: '0 auto', marginTop: '1rem' }}
                      className="cp-AddElementButton"
                      onClick={() => {
                        pageTrades.current += 1
                        fetchTrades()
                      }}
                    >
                      View more
                    </button>
                  )}

                  {!hasMoreTrades && !tradesLoading && trades.length > 0 && (
                    <p style={{ textAlign: 'center', margin: '1rem 0' }}>
                      No more trades to show
                    </p>
                  )}

                  {!tradesLoading && trades.length === 0 && (
                    <p style={{ textAlign: 'center', margin: '1rem 0' }}>
                      No trades available
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default Feed
